import React, { useEffect, useState, useRef } from "react";
import { Loader2, RefreshCw, Share2, Eye, Camera, Trash2, ShieldCheck, ShieldOff, AlertCircle, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { ContactPickerDialog } from "@/components/shared/ContactPickerDialog";
import { buildContactOptions } from "@/lib/contactActions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const normalisePhoneForWa = (raw, defaultCountryCode) => {
  let s = String(raw || "").replace(/[^\d+]/g, "");
  if (!s) return "";
  if (s.startsWith("+")) return s.slice(1);
  if (s.startsWith("00")) return s.slice(2);
  const cc = String(defaultCountryCode || "").replace(/[^\d]/g, "");
  if (s.length === 10 && cc) return cc + s;
  return s;
};

/**
 * Pickup Authorization section inside Student Profile / Edit dialog.
 * Admin-only controls: toggle, photo upload, generate/regenerate QR, view, share.
 */
export const StudentPickupSection = ({ student, onUpdated }) => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState({}); // {qr_token, qr_version, qr_generated_at, display_id, pickup_authorization_enabled}
  const [showQr, setShowQr] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [photoZoomOpen, setPhotoZoomOpen] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const { default_country_code, organization_name, display_name } = useBrand();

  useEffect(() => {
    if (!student?.user_id) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/students/${student.user_id}/pickup-qr`)
      .then((r) => { if (!cancelled) setInfo(r.data); })
      .catch(() => { if (!cancelled) setInfo({}); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [student?.user_id]);

  const enabled = !!info.pickup_authorization_enabled;
  const hasQr = !!info.qr_token;
  const displayId = info.display_id || student?.display_id || "—";
  const studentName = student?.name || "Student";

  const toggleEnabled = async (next) => {
    setBusy(true);
    try {
      const r = await api.patch(`/students/${student.user_id}/pickup-authorization`, { enabled: next });
      const s = r.data?.student || {};
      setInfo((p) => ({ ...p, pickup_authorization_enabled: !!s.pickup_authorization_enabled, display_id: s.display_id || p.display_id }));
      toast.success(next ? "Pickup authorization enabled" : "Pickup authorization disabled");
      onUpdated?.(s);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally { setBusy(false); }
  };

  const generate = async ({ regen = false } = {}) => {
    setBusy(true);
    try {
      const r = await api.post(`/students/${student.user_id}/pickup-qr/generate`);
      setInfo((p) => ({ ...p, qr_token: r.data.qr_token, qr_version: r.data.qr_version, display_id: r.data.display_id, qr_generated_at: new Date().toISOString() }));
      toast.success(regen ? "QR regenerated. Previous QR is now invalid." : "QR generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate QR");
    } finally { setBusy(false); }
  };

  const handlePhotoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3_000_000) { toast.error("Photo must be under 3 MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try {
        await api.post(`/students/${student.user_id}/photo`, { photo_data: reader.result });
        toast.success("Photo updated");
        onUpdated?.({ photo_data: String(reader.result || "").split(",")[1] });
      } catch (err) {
        toast.error(err.response?.data?.detail || "Photo upload failed");
      } finally {
        setBusy(false);
        if (fileRef.current) fileRef.current.value = "";
        if (cameraRef.current) cameraRef.current.value = "";
      }
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = async () => {
    setBusy(true);
    try {
      await api.post(`/students/${student.user_id}/photo`, { photo_data: "" });
      toast.success("Photo removed");
      onUpdated?.({ photo_data: null });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const publicUrl = info.qr_token ? `${window.location.origin}/p/${info.qr_token}` : "";
  const qrPngUrl = info.qr_token ? `${API}/public/pickup-qr/${info.qr_token}/image` : "";

  // ----------------------------------------------------------------------
  // Pickup QR WhatsApp share (Feb 2026 Feature 6).
  //
  // Recipient picker added BEFORE WhatsApp is invoked. The existing message
  // template + deep-link behaviour is preserved verbatim — only the choice
  // of phone number changes based on which recipient was picked.
  // ----------------------------------------------------------------------
  const [qrPicker, setQrPicker] = useState(false);

  const buildQrMessage = (recipientName) => {
    const orgName = display_name || organization_name || "";
    return (
`Hello ${recipientName || studentName},

Please find the pickup authorization QR code for ${studentName} below.

Please show this QR code during student pickup.

Student ID: ${displayId}

${publicUrl}

Thank you,
${orgName}`
    );
  };

  const fireWhatsAppShare = (rawPhone, recipientName) => {
    const phone = normalisePhoneForWa(rawPhone, default_country_code);
    if (!phone) { toast.error("Valid phone number not available"); return; }
    const text = buildQrMessage(recipientName);
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win) {
      toast.message("Pop-up was blocked", {
        description: "Tap below to open WhatsApp.",
        action: { label: "Open WhatsApp", onClick: () => window.open(url, "_blank", "noopener,noreferrer") },
      });
    }
  };

  const handleShare = () => {
    if (!info.qr_token) { toast.error("Generate the QR first"); return; }
    // Build options from the LATEST persisted student doc (passed in via prop —
    // parent re-hydrates after every profile save, so no stale phone).
    const options = buildContactOptions({
      studentName,
      studentPhone: student?.phone,
      guardianName: student?.parent_name || "Guardian",
      guardianPhone: student?.parent_phone,
    });
    if (options.length === 0) {
      toast.error("Valid phone number not available");
      return;
    }
    if (options.length === 1) {
      const onlyKey = options[0].key;
      const phone = onlyKey === "student" ? student?.phone : student?.parent_phone;
      const name = onlyKey === "student" ? studentName : (student?.parent_name || studentName);
      fireWhatsAppShare(phone, name);
      return;
    }
    setQrPicker(true);
  };

  const handleQrPickerPick = (recipientKey) => {
    const phone = recipientKey === "student" ? student?.phone : student?.parent_phone;
    const name = recipientKey === "student" ? studentName : (student?.parent_name || studentName);
    fireWhatsAppShare(phone, name);
  };

  const handleDownload = async () => {
    if (!qrPngUrl) return;
    try {
      const res = await fetch(qrPngUrl);
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `${studentName.replace(/\s+/g, "_")}_${displayId}_QR.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 60_000);
    } catch (e) { toast.error("Download failed"); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pickup-section">
      {/* Display ID */}
      <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Student ID</p>
          <p className="text-sm font-bold text-slate-900" data-testid="pickup-display-id">{displayId}</p>
        </div>
        <ShieldCheck className="w-5 h-5 text-violet-600" />
      </div>

      {/* Toggle */}
      <div className="flex items-center justify-between p-3 rounded-xl border border-slate-200">
        <div>
          <p className="text-sm font-semibold text-slate-900">Pickup Authorization</p>
          <p className="text-xs text-slate-500 mt-0.5">When ON, this student requires QR verification for pickup.</p>
        </div>
        <Switch
          data-testid="pickup-auth-toggle"
          checked={enabled}
          disabled={busy}
          onCheckedChange={toggleEnabled}
        />
      </div>

      {/* Photo */}
      <div className="p-3 rounded-xl border border-slate-200">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => student?.photo_data && setPhotoZoomOpen(true)}
            className={`w-16 h-16 rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center flex-shrink-0 ${student?.photo_data ? "cursor-zoom-in hover:ring-2 hover:ring-violet-300 transition-all" : ""}`}
            data-testid="pickup-photo-thumb"
            aria-label={student?.photo_data ? "View photo" : "No photo"}
          >
            {student?.photo_data ? (
              <img src={`data:image/jpeg;base64,${student.photo_data}`} alt="Student" className="w-full h-full object-cover" />
            ) : (
              <Camera className="w-6 h-6 text-slate-300" />
            )}
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900">Student Photo</p>
            <p className="text-xs text-slate-500 mt-0.5">Shown during pickup verification.{student?.photo_data ? " Tap to enlarge." : ""}</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <input ref={fileRef} type="file" accept="image/*" onChange={handlePhotoFile} className="hidden" data-testid="pickup-photo-file" />
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handlePhotoFile} className="hidden" data-testid="pickup-photo-camera" />
            <Button size="sm" variant="outline" className="rounded-full text-xs h-8" onClick={() => cameraRef.current?.click()} disabled={busy} data-testid="pickup-take-photo-btn">
              <Camera className="w-3 h-3 mr-1" /> Take Photo
            </Button>
            <Button size="sm" variant="outline" className="rounded-full text-xs h-8" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="pickup-upload-btn">
              <Download className="w-3 h-3 mr-1 rotate-180" /> Upload
            </Button>
            {student?.photo_data && (
              <Button size="sm" variant="ghost" className="rounded-full text-xs h-8 text-red-600 hover:bg-red-50" onClick={removePhoto} disabled={busy}>
                <Trash2 className="w-3 h-3 mr-1" /> Remove
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Photo zoom dialog */}
      <Dialog open={photoZoomOpen} onOpenChange={setPhotoZoomOpen}>
        <DialogContent className="max-w-2xl p-0 bg-transparent border-0 shadow-none">
          <DialogHeader className="sr-only">
            <DialogTitle>{studentName} — Photo</DialogTitle>
          </DialogHeader>
          {student?.photo_data && (
            <img
              src={`data:image/jpeg;base64,${student.photo_data}`}
              alt={studentName}
              className="w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl"
              data-testid="pickup-photo-zoomed"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* QR */}
      <div className="p-3 rounded-xl border border-slate-200 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900">Pickup QR Code</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {hasQr
                ? `Active QR · v${info.qr_version}`
                : "Not generated yet"}
            </p>
          </div>
          {!hasQr ? (
            <Button size="sm" onClick={() => generate({})} disabled={busy} data-testid="pickup-generate-qr" className="rounded-full bg-violet-600 hover:bg-violet-700 text-white text-xs">
              <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Generate QR
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setConfirmRegen(true)} disabled={busy} data-testid="pickup-regenerate-qr" className="rounded-full text-xs">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerate
            </Button>
          )}
        </div>

        {hasQr && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowQr(true)} className="rounded-full text-xs" data-testid="pickup-view-qr">
              <Eye className="w-3.5 h-3.5 mr-1" /> View
            </Button>
            <Button size="sm" variant="outline" onClick={handleDownload} className="rounded-full text-xs" data-testid="pickup-download-qr">
              <Download className="w-3.5 h-3.5 mr-1" /> Download
            </Button>
            <Button size="sm" onClick={handleShare} className="rounded-full text-xs bg-green-600 hover:bg-green-700 text-white" data-testid="pickup-share-qr">
              <Share2 className="w-3.5 h-3.5 mr-1" /> Share via WhatsApp
            </Button>
          </div>
        )}

        {!enabled && hasQr && (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 flex gap-2">
            <ShieldOff className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            Pickup Authorization is OFF — QR exists but won't be required for pickup. Toggle ON above.
          </div>
        )}
      </div>

      {/* QR view modal */}
      <Dialog open={showQr} onOpenChange={setShowQr}>
        <DialogContent className="max-w-sm w-[92vw]" data-testid="pickup-qr-modal">
          <DialogHeader>
            <DialogTitle className="text-base font-heading">Pickup QR Code</DialogTitle>
          </DialogHeader>
          <div className="text-center space-y-3">
            {qrPngUrl ? (
              <img src={qrPngUrl} alt={`QR for ${studentName}`} className="w-64 h-64 mx-auto bg-white border border-slate-200 rounded-xl p-2" />
            ) : (
              <p className="text-slate-500 text-sm">No QR available</p>
            )}
            <div>
              <p className="text-xs text-slate-500">Student Name</p>
              <p className="text-base font-bold text-slate-900">{studentName}</p>
              <p className="text-xs text-slate-500 mt-2">Student ID</p>
              <p className="text-base font-bold text-violet-700">{displayId}</p>
            </div>
            <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
              <Button onClick={handleDownload} className="w-full sm:flex-1 rounded-full bg-violet-600 hover:bg-violet-700 text-white">
                <Download className="w-4 h-4 mr-1" /> Download
              </Button>
              <Button onClick={handleShare} className="w-full sm:flex-1 rounded-full bg-green-600 hover:bg-green-700 text-white">
                <Share2 className="w-4 h-4 mr-1" /> Share
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Regenerate confirmation */}
      <Dialog open={confirmRegen} onOpenChange={setConfirmRegen}>
        <DialogContent className="max-w-sm w-[92vw]">
          <DialogHeader>
            <DialogTitle className="text-base font-heading">Regenerate QR?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600 flex gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500 shrink-0" />
              The previous QR will become invalid <strong>immediately</strong>. Anyone holding it will need a new copy.
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
              <Button variant="outline" onClick={() => setConfirmRegen(false)} className="w-full sm:flex-1 rounded-full" disabled={busy}>Cancel</Button>
              <Button onClick={async () => { await generate({ regen: true }); setConfirmRegen(false); }} className="w-full sm:flex-1 rounded-full bg-violet-600 hover:bg-violet-700 text-white" disabled={busy} data-testid="pickup-regenerate-confirm">
                <RefreshCw className="w-4 h-4 mr-1" /> Regenerate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <ContactPickerDialog
        open={qrPicker}
        onOpenChange={setQrPicker}
        title="Who would you like to send the Pickup QR Code to?"
        options={buildContactOptions({
          studentName,
          studentPhone: student?.phone,
          guardianName: student?.parent_name || "Guardian",
          guardianPhone: student?.parent_phone,
        })}
        onPick={handleQrPickerPick}
      />
    </div>
  );
};

export default StudentPickupSection;
